import { useState, useEffect, useRef } from 'react';
import { Icon } from "semantic-ui-react";
import NotificationItem from './NotificationItem';

const NotificationsList = ({
    loading,
    notifications,
    pageSize = 7,
    hasUnreadButton = false,
    setNotificationRead,
    autoFocus = false,
    getPlugin,
    onNotificationClick,
    onPageChange,
    onClose,
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const containerRef = useRef(null);
    useEffect(() => {
        if (autoFocus && containerRef.current) {
            const firstButton = containerRef.current.querySelector('button');
            firstButton?.focus?.();
        }
    }, []);

    const [visibleNotifs, setVisibleNotifs] = useState(notifications.slice(0, pageSize));
    const [hasMore, setMore] = useState(notifications.length > pageSize)

    const loadMore = () => {
        const next = notifications.slice(visibleNotifs.length, visibleNotifs.length + pageSize);
        setVisibleNotifs(prev => [...prev, ...next]);
        if (visibleNotifs.length + next.length >= notifications.length) setMore(false);
    }

    const handleScroll = () => {
        if (!containerRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
        const threshold = 100;
        if (scrollTop + clientHeight >= scrollHeight - threshold && hasMore) {
            loadMore()
        }
    }

    useEffect(() => {
        const cont = containerRef.current;
        if (containerRef.current) {
            cont.addEventListener("scroll", handleScroll);
            return () => cont.removeEventListener("scroll", handleScroll);
        }
    }, [visibleNotifs, hasMore]);

    if (loading) {
        return (
            <div className="flex justify-center p-8">
                <Icon name="spinner" loading size="large" title="Loading notifications" data-testid="loading" className="text-is2-gray-body/50" />
            </div>
        );
    }

    if (notifications.length === 0) {
        return (
            <div className="text-center p-8 text-gray-500">
                No notifications to display
            </div>
        );
    }

    return (
        <article data-testid="notifications-loaded" ref={containerRef} className="notifications-list column overflow-y-auto pb-6 sm:pb-0 max-h-[calc(100vh-242px)] lg:max-h-[80vh] lg:rounded-b-[28px]">
            <ul className="[&>li]:p-0 [&>li]:m-0 !pl-0 " role="list" aria-label="Notifications">
                {visibleNotifs.map((notification) => (
                    <li
                        key={notification.notification_id}
                        role="listitem"
                        className={`[&>button]:w-full border-b last:border-b-0 ${(notification.isDynamic || notification.read)
                            ? 'border-white'
                            : 'border-[#ECF4FE]'
                            }`}
                    >
                        <NotificationItem
                            notification={notification}
                            onClick={onNotificationClick}
                            hasUnreadButton={hasUnreadButton}
                            setNotificationRead={setNotificationRead}
                            getPlugin={getPlugin}
                            onClose={onClose}
                        />
                    </li>
                ))}
                {hasMore && <div className="flex justify-center p-8 text-gray">
                    <Icon name="spinner" loading className="text-is2-gray-body/50" />
                </div>}
            </ul>
        </article>
    );
}

export const getPaginatedNotifications = (notifications, currentPage, pageSize) => {
    return notifications.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );
};

export default NotificationsList;
